import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { SectionHeader, StatTile, Pill } from "@/components/Brand";
import { toast } from "sonner";
import {
  IndianRupee,
  ShieldOff,
  AlertTriangle,
  CreditCard,
  Trash2,
} from "lucide-react";

export default function Monetization() {
  const [price, setPrice] = useState(0);
  const [purchases, setPurchases] = useState([]);
  const [users, setUsers] = useState([]);
  const [wards, setWards] = useState([]);
  const [grant, setGrant] = useState({ user_id: "", ward_id: "" });

  const load = async () => {
    const [s, p, u, w] = await Promise.all([
      api.get("/admin/settings"),
      api.get("/admin/purchases"),
      api.get("/admin/users?role=candidate"),
      api.get("/admin/wards"),
    ]);
    setPrice(s.data.report_price_inr);
    setPurchases(p.data);
    setUsers(u.data);
    setWards(w.data);
  };

  useEffect(() => {
    load();
  }, []);

  const savePrice = async () => {
    try {
      await api.put("/admin/settings", { report_price_inr: Number(price) });
      toast.success("Price updated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doGrant = async () => {
    if (!grant.user_id || !grant.ward_id) return toast.error("Pick user and ward");
    try {
      await api.post("/admin/purchases/grant", grant);
      toast.success("Access granted");
      setGrant({ user_id: "", ward_id: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke access?")) return;
    await api.post(`/admin/purchases/${id}/revoke`);
    toast.success("Access revoked");
    load();
  };

  const clearTemp = async (scope) => {
    const msgs = {
      surveys: "Delete all survey responses? Wards, booths and users are kept.",
      all_data:
        "Delete ALL surveys, purchases, audit logs and candidate requests? Users & wards/booths are kept.",
      factory_reset:
        "FACTORY RESET — this will delete EVERY ward, booth, surveyor, candidate, survey, purchase and audit log. Only your admin account survives. Continue?",
    };
    if (!window.confirm(msgs[scope] || "Are you sure?")) return;
    try {
      const r = await api.post(`/admin/clear-temp-data?scope=${scope}`);
      toast.success(`Cleared: ${JSON.stringify(r.data.counts)}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const revenue = purchases.filter((p) => p.status === "active" && p.method !== "admin_grant").reduce((a, p) => a + (p.amount_inr || 0), 0);

  return (
    <div>
      <SectionHeader eyebrow="Revenue" title="Monetization" />

      <div className="wi-stat-row mb-6">
        <StatTile label="Report Price (INR)" value={`₹${price}`} icon={IndianRupee} />
        <StatTile label="Total Purchases" value={purchases.length} icon={CreditCard} />
        <StatTile label="Revenue (mocked)" value={`₹${revenue}`} hint="Excludes admin grants" />
        <StatTile label="Active Candidates" value={users.length} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="wi-card p-5">
          <div className="data-label">Pricing</div>
          <h3 className="text-base font-semibold text-slate-900 mt-1 mb-3">Set price per ward report</h3>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <span className="data-label">Amount (INR)</span>
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                data-testid="report-price-input"
                className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            <button
              onClick={savePrice}
              data-testid="save-price-btn"
              className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Save
            </button>
          </div>
        </div>

        <div className="wi-card p-5">
          <div className="data-label">Manual Grant</div>
          <h3 className="text-base font-semibold text-slate-900 mt-1 mb-3">Grant a candidate ward access</h3>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={grant.user_id}
              onChange={(e) => setGrant({ ...grant, user_id: e.target.value })}
              data-testid="grant-user-select"
              className="border border-slate-300 rounded-md px-2 py-2 text-sm"
            >
              <option value="">Candidate…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.email}</option>)}
            </select>
            <select
              value={grant.ward_id}
              onChange={(e) => setGrant({ ...grant, ward_id: e.target.value })}
              data-testid="grant-ward-select"
              className="border border-slate-300 rounded-md px-2 py-2 text-sm"
            >
              <option value="">Ward…</option>
              {wards.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
          <button
            onClick={doGrant}
            data-testid="grant-access-btn"
            className="mt-3 inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Grant access
          </button>
        </div>
      </div>

      <div className="wi-card overflow-x-auto mb-8">
        <div className="p-5 border-b border-slate-200">
          <div className="data-label">Transactions</div>
          <h3 className="text-base font-semibold text-slate-900 mt-1">All purchases & grants</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Candidate", "Ward", "Amount", "Method", "Status", "Date", ""].map((h) => (
                <th key={h} className="text-left px-4 py-3 data-label">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {purchases.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">No transactions yet.</td></tr>}
            {purchases.map((p) => (
              <tr key={p.id} className="border-b border-slate-100" data-testid={`purchase-row-${p.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{p.user_email}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{p.ward_name}</td>
                <td className="px-4 py-3 font-mono">₹{p.amount_inr}</td>
                <td className="px-4 py-3 text-xs"><Pill tone={p.method === "admin_grant" ? "blue" : "green"}>{p.method}</Pill></td>
                <td className="px-4 py-3"><Pill tone={p.status === "active" ? "green" : "red"}>{p.status}</Pill></td>
                <td className="px-4 py-3 text-xs font-mono text-slate-500">{p.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 text-right">
                  {p.status === "active" && (
                    <button onClick={() => revoke(p.id)} data-testid={`revoke-${p.id}`} className="text-red-600 hover:bg-red-50 p-1.5 rounded" aria-label="Revoke">
                      <ShieldOff size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="wi-card p-5 border-red-200 bg-red-50/30">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-red-600 mt-0.5" size={18} />
          <div className="flex-1">
            <div className="data-label text-red-700">Danger Zone</div>
            <h3 className="text-base font-semibold text-slate-900 mt-1">Delete temp data</h3>
            <p className="text-sm text-slate-600 mt-1">
              Wipe seeded sample surveys to start fresh, or clear everything except wards/booths/users.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={() => clearTemp("surveys")}
                data-testid="clear-surveys-btn"
                className="inline-flex items-center gap-1.5 bg-white border border-red-200 hover:bg-red-50 text-red-700 px-3 py-1.5 rounded-md text-sm font-medium"
              >
                <Trash2 size={14} /> Clear all surveys
              </button>
              <button
                onClick={() => clearTemp("all_data")}
                data-testid="clear-all-btn"
                className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-md text-sm font-medium"
              >
                <Trash2 size={14} /> Clear ALL data (keep wards & users)
              </button>
              <button
                onClick={() => clearTemp("factory_reset")}
                data-testid="factory-reset-btn"
                className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-black text-white px-3 py-1.5 rounded-md text-sm font-medium"
              >
                <Trash2 size={14} /> Factory reset (wipe everything except admin)
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
